// src/components/CookiesPolicy.tsx
import React from "react";

const sections = [
  { id: "introduction", label: "Introduction" },
  { id: "what-are-cookies", label: "What Are Cookies?" },
  { id: "types-of-cookies", label: "Types of Cookies We Use" },
  { id: "how-we-use-cookies", label: "How We Use Cookies" },
  { id: "third-party-cookies", label: "Third-Party Cookies" },
  { id: "cookie-management", label: "How to Manage Your Cookie Preferences" },
  { id: "data-retention", label: "Cookie Data Retention" },
  { id: "policy-updates", label: "Policy Updates" },
  { id: "contact", label: "Contact Information" },
];

const headingStyle = {
  fontWeight: 700,
  fontSize: "1.1rem",
  marginTop: "2.5rem",
  marginBottom: "1rem",
  color: "#222",
  borderBottom: "1px solid #eee",
  paddingBottom: "0.25rem",
  letterSpacing: ".01em",
};

const linkStyle = { color: "#0077cc", textDecoration: "underline", fontWeight: 600 };

const CookiesPolicy: React.FC = () => (
  <div style={{ maxWidth: 900, margin: "auto", padding: "2.5rem 1rem", background: "#fff", color: "#222", fontFamily: "Segoe UI, Arial, sans-serif" }}>
    <h1 style={{ textAlign: "center", marginBottom: "1.5rem", fontWeight: 800, fontSize: "1.5rem" }}>
      Cookies Policy – Crushhh.com
    </h1>
    <p style={{ textAlign: "center", fontStyle: "italic", marginBottom: "2rem" }}>
      Effective Date: [06/22/2025]
    </p>

    {/* Jump-to-section navigation */}
    <nav style={{ margin: "2rem 0 2.5rem 0", borderBottom: "1px solid #eee", paddingBottom: "1rem" }}>
      <ul style={{ listStyle: "none", padding: 0, display: "flex", flexWrap: "wrap", gap: "1.5rem", justifyContent: "center" }}>
        {sections.map((s) => (
          <li key={s.id}>
            <a href={`#${s.id}`} style={{ ...linkStyle, fontSize: "1.1rem" }}><b>{s.label}</b></a>
          </li>
        ))}
      </ul>
    </nav>

    {/* Introduction */}
    <section id="introduction">
      <h2 style={headingStyle}><b>Introduction</b></h2>
      <p>
        This Cookies Policy explains how Crushhh Inc. (“Crushhh”, “we”, “us”, or “our”) uses cookies and similar tracking technologies on our website and app. We are committed to transparency and protecting your privacy. Please review this policy alongside our <a href="/privacy" style={linkStyle}>Privacy Policy</a> and <a href="/terms" style={linkStyle}>Terms & Conditions</a>.
      </p>
    </section>

    {/* What Are Cookies? */}
    <section id="what-are-cookies">
      <h2 style={headingStyle}><b>What Are Cookies?</b></h2>
      <p>
        Cookies are small text files that are stored on your device (computer, smartphone, or tablet) when you visit a website. They help websites recognize your device, remember your preferences, and enhance your user experience. Cookies can also collect information about your browsing activities and enable certain website features.
      </p>
      <p>
        In addition to cookies, we may use similar technologies such as web beacons, pixels, and local storage, which function in a comparable way.
      </p>
    </section>

    {/* Types of Cookies We Use */}
    <section id="types-of-cookies">
      <h2 style={headingStyle}><b>Types of Cookies We Use</b></h2>
      <ul>
        <li>
          <b>Strictly Necessary Cookies:</b> Essential for the operation of our website and app. These cookies enable core functions like security, network management, and accessibility. They cannot be switched off in our systems.
        </li>
        <li>
          <b>Functional Cookies:</b> Allow us to remember your preferences (such as language or region) and provide enhanced, personalized features.
        </li>
        <li>
          <b>Performance & Analytics Cookies:</b> Collect information about how you use our website (such as which pages you visit and if you experience errors). This helps us improve site performance and user experience.
        </li>
        <li>
          <b>Marketing & Advertising Cookies:</b> Used to deliver relevant ads to you and measure the effectiveness of our marketing campaigns, including on third-party platforms.
        </li>
        <li>
          <b>Session Cookies:</b> Temporary cookies that are erased when you close your browser.
        </li>
        <li>
          <b>Persistent Cookies:</b> Remain on your device for a set period or until you delete them.
        </li>
      </ul>
      <p>
        For a current list of cookies and their purposes, please contact us at <a href="mailto:dpo@crushhh.com" style={linkStyle}>dpo@crushhh.com</a>.
      </p>
    </section>

    {/* How We Use Cookies */}
    <section id="how-we-use-cookies">
      <h2 style={headingStyle}><b>How We Use Cookies</b></h2>
      <ul>
        <li>To enable essential website functions and ensure security.</li>
        <li>To remember your preferences and settings.</li>
        <li>To analyze traffic, usage patterns, and improve our services.</li>
        <li>To personalize your experience and provide relevant content.</li>
        <li>To deliver targeted advertising and measure campaign effectiveness.</li>
        <li>To detect and prevent fraud or misuse of our services.</li>
      </ul>
      <p>
        We do not use cookies to collect sensitive personal information such as passwords or payment details.
      </p>
    </section>

    {/* Third-Party Cookies */}
    <section id="third-party-cookies">
      <h2 style={headingStyle}><b>Third-Party Cookies</b></h2>
      <p>
        Some cookies on our website are set by third parties, such as analytics providers (e.g., Google Analytics), advertising networks, and social media platforms. These third parties may use cookies to collect information about your online activities over time and across different websites. We do not control these third-party cookies and recommend that you review their privacy and cookie policies for more information.
      </p>
      <p>
        We take steps to ensure that third-party cookies are used in accordance with our policies and applicable laws.
      </p>
    </section>

    {/* Cookie Management */}
    <section id="cookie-management">
      <h2 style={headingStyle}><b>How to Manage Your Cookie Preferences</b></h2>
      <ul>
        <li>
          <b>Consent Banner:</b> When you first visit our site, you will see a cookie consent banner. You can accept all cookies, reject non-essential cookies, or customize your preferences.
        </li>
        <li>
          <b>Change Settings:</b> You can update your cookie preferences at any time via our cookie settings tool or banner.
        </li>
        <li>
          <b>Browser Controls:</b> Most browsers allow you to block or delete cookies via their settings. Please note that blocking certain cookies may affect the functionality of our site.
        </li>
        <li>
          <b>Opt-Out Tools:</b> For some third-party cookies, you may opt out directly with the provider (e.g., Google Analytics Opt-Out Browser Add-on).
        </li>
      </ul>
      <p>
        For more information or assistance with cookie preferences, please contact <a href="mailto:dpo@crushhh.com" style={linkStyle}>dpo@crushhh.com</a>.
      </p>
    </section>

    {/* Cookie Data Retention */}
    <section id="data-retention">
      <h2 style={headingStyle}><b>Cookie Data Retention</b></h2>
      <p>
        Cookies are retained only as long as necessary for their intended purpose. Session cookies are deleted when you close your browser. Persistent cookies remain on your device for a set period or until you delete them. We review our cookie use regularly and update expiration dates to comply with legal and business requirements.
      </p>
    </section>

    {/* Policy Updates */}
    <section id="policy-updates">
      <h2 style={headingStyle}><b>Policy Updates</b></h2>
      <p>
        We may update this Cookies Policy from time to time to reflect changes in our practices, technology, or legal requirements. When we make material changes, we will notify you by updating the effective date at the top of this page and, where appropriate, by additional notice (such as a banner or email).
      </p>
    </section>

    {/* Contact Information */}
    <section id="contact">
      <h2 style={headingStyle}><b>Contact Information</b></h2>
      <p>
        If you have any questions, concerns, or requests regarding this Cookies Policy or our use of cookies, please contact our Data Protection Officer:
      </p>
      <ul>
        <li><b>Email:</b> <a href="mailto:dpo@crushhh.com" style={linkStyle}>dpo@crushhh.com</a></li>
        <li><b>Business Address:</b> CRUSHHH INC, 5830 E 2ND ST, STE 7000, CASPER, WY 82609</li>
      </ul>
    </section>
  </div>
);

export default CookiesPolicy;
